// src/components/ErrorBoundary/ErrorBoundary.jsx
// Captura errores de renderizado en cualquier componente hijo y muestra
// un fallback amigable en lugar de romper toda la UI.

import { Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Registra el error para diagnóstico (sin exponer datos sensibles al usuario)
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { fallback } = this.props;

    // Si se provee un fallback personalizado, usarlo
    if (fallback) {
      return typeof fallback === 'function'
        ? fallback({ error: this.state.error, reset: this.handleReset })
        : fallback;
    }

    // Fallback por defecto
    return (
      <div
        role="alert"
        className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center text-[var(--color-text)]"
      >
        <div className="text-[3rem]">⚠️</div>
        <h2 className="m-0 text-xl font-semibold text-[var(--color-danger)]">
          Algo salió mal
        </h2>
        <p className="m-0 max-w-[480px] text-[var(--color-text-muted)]">
          Se produjo un error inesperado en esta sección. Puedes intentar recargar o regresar al
          inicio.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={this.handleReset}
            className="cursor-pointer rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-5 py-2 text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-gray-50"
          >
            Reintentar
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            className="cursor-pointer rounded-[var(--radius-md)] border-none bg-[var(--color-primary)] px-5 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Ir al Inicio
          </button>
        </div>
        {process.env.NODE_ENV === 'development' && this.state.error && (
          <details className="mt-4 w-full max-w-[640px] text-left">
            <summary className="cursor-pointer text-sm text-[var(--color-text-muted)]">
              Detalles del error (solo en desarrollo)
            </summary>
            <pre className="mt-2 overflow-x-auto break-words whitespace-pre-wrap rounded-[var(--radius-sm)] bg-[var(--color-danger-bg)] p-4 text-xs text-[var(--color-danger)]">
              {this.state.error.toString()}
            </pre>
          </details>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
